<template>
    <!-- Mounted throughout and sr-only while idle: a polite region that appears already holding
         its text is not reliably announced. -->
    <div :class="show ? 'full-page-loader' : 'sr-only'" ref="rootEl" role="status" tabindex="-1">
        <div v-if="show" class="loader-content">
            <SharedSpinner size="lg" />
            <div class="loader-text">
                <h3 class="text-lg font-semibold text-gray-900 mb-2">Switching account</h3>
                <p class="text-gray-600">Loading user data...</p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { nextTick, onUnmounted, ref, watch } from 'vue';

interface Props {
    show: boolean;
}

const props = defineProps<Props>();

const rootEl = ref<HTMLElement | null>(null);

let restoreFocusTo: HTMLElement | null = null;
let inerted: HTMLElement[] = [];

// The overlay paints over everything but does not remove it, so every ancestor's siblings have to be
// inerted or Tab walks into controls nobody can see.
function inertBehind(el: HTMLElement) {
    for (let node: HTMLElement | null = el; node && node !== document.body; node = node.parentElement) {
        for (const sibling of Array.from(node.parentElement?.children ?? [])) {
            if (sibling !== node && sibling instanceof HTMLElement && !sibling.inert) {
                sibling.inert = true;
                inerted.push(sibling);
            }
        }
    }
}

// `.header-acc` is rendered twice — the drawer's copy comes first in the document and is
// display:none above lg, or inside the closed drawer's inert subtree below it — so the first
// match is the one that cannot take focus. Existence is not reachability: the target has to be
// rendered and outside every inert subtree, or focus() is a no-op and stays on the loader.
function reachable(el: HTMLElement | null | undefined): el is HTMLElement {
    return !!el?.isConnected && el.getClientRects().length > 0 && !el.closest('[inert]');
}

function releaseInert() {
    inerted.forEach((el) => {
        el.inert = false;
    });
    inerted = [];
}

// The overlay covers every control, so focus has to follow it or the page reads as frozen, and it has
// to come back when the overlay goes or focus is left on <body>.
watch(
    () => props.show,
    (visible) => {
        if (visible) {
            restoreFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            nextTick(() => {
                // The root is mounted throughout — sr-only while idle — so it is still here even
                // after a close. Without the re-check, a switch that resolves inside the same
                // render cycle inerts the page behind a loader that has already gone, and the
                // closing branch has already run with nothing to release.
                if (!rootEl.value || !props.show) {
                    return;
                }

                rootEl.value.focus();
                inertBehind(rootEl.value);
            });

            return;
        }

        releaseInert();
        // The switch that opened this loader can replace the control that started it — the
        // account item disappears with `v-if="!impersonating"` — and focus() on a node the
        // browser will not focus does nothing, leaving focus on a loader that is about to go
        // sr-only. Fall back to whatever the page now offers that can actually take it.
        // Queried in preference order rather than as one selector list: the header trigger sits
        // inside <main>, so a combined query would return the landmark first on document order
        // and the account control would never be reached.
        const target = reachable(restoreFocusTo)
            ? restoreFocusTo
            : [
                ...document.querySelectorAll<HTMLElement>('.header-acc'),
                ...document.querySelectorAll<HTMLElement>('main#main-content'),
            ].find(reachable);

        target?.focus();
        restoreFocusTo = null;
    }
);

// Unmounting while shown would otherwise leave the page inert with nothing left to release it.
onUnmounted(releaseInert);
</script>

<style lang="scss" scoped>
.full-page-loader {
    @apply fixed inset-0 z-[9999] flex items-center justify-center bg-white;
    
    .loader-content {
        @apply flex flex-col items-center gap-6;
        
        .loader-text {
            @apply text-center;
        }
    }
}
</style> 